import React, { useEffect, useState } from "react";
import api from "../api";
import { PageHeader, EmptyState, inr } from "../components/UI";
import { AlertTriangle, Phone, MessageCircle, Sparkles, Clock } from "lucide-react";
import { Link } from "react-router-dom";

const severityMeta = {
  critical: { cls: "bg-red-100 text-red-800", bar: "bg-red-500" },
  warning: { cls: "bg-amber-100 text-amber-800", bar: "bg-amber-500" },
};

const tierMeta = {
  premium: { cls: "bg-purple-100 text-purple-800", label: "Premium" },
  normal: { cls: "bg-slate-100 text-slate-700", label: "Normal" },
  low: { cls: "bg-red-50 text-red-700", label: "Low" },
};

export default function ChurnAlerts() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);

  const load = (d) => {
    setLoading(true);
    api.get(`/churn/alerts?days=${d}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(days); }, [days]);

  return (
    <div className="p-4 lg:p-8 max-w-7xl" data-testid="churn-page">
      <PageHeader
        title="Churn Alerts"
        subtitle="Regular customers who suddenly stopped ordering — win them back before they go to competition"
      />

      <div className="flex flex-wrap gap-2 mb-5 items-center">
        <span className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Silent for:</span>
        {[30, 60, 90, 120, 180].map((d) => (
          <button key={d} data-testid={`churn-days-${d}`} onClick={() => setDays(d)}
            className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider font-semibold transition ${days === d ? "bg-[#C1121F] text-white" : "bg-white border border-[#E5E7EB] text-[#475569] hover:border-[#C1121F]"}`}>
            {d}+ days
          </button>
        ))}
      </div>

      {data && (
        <div className="grid grid-cols-3 gap-3 mb-6 stagger">
          <div className="card-flat p-4" data-testid="kpi-churn-total">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">At Risk</div>
            <div className="font-display font-bold text-2xl mt-1">{data.total}</div>
          </div>
          <div className="card-flat p-4" data-testid="kpi-churn-critical">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">Critical</div>
            <div className="font-display font-bold text-2xl mt-1 text-red-600">{data.critical}</div>
          </div>
          <div className="card-flat p-4" data-testid="kpi-churn-warning">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">Warning</div>
            <div className="font-display font-bold text-2xl mt-1 text-amber-600">{data.warning}</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-[#475569]">Analyzing customer activity…</div>
      ) : !data || data.items.length === 0 ? (
        <div data-testid="churn-empty">
          <EmptyState icon={Sparkles} title="No churn risk detected 🎉" hint="All your regular customers are ordering on time. Keep it up!" />
        </div>
      ) : (
        <div className="space-y-3" data-testid="churn-list">
          {data.items.map((c) => {
            const sm = severityMeta[c.severity];
            const tm = tierMeta[c.customer_tier] || tierMeta.normal;
            return (
              <div key={c.customer_id} className={`card-flat p-4 border-l-4 ${sm.bar.replace("bg-", "border-l-")}`} data-testid={`churn-row-${c.customer_id}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-bold text-lg">{c.customer_name}</h3>
                      <span className={`badge ${tm.cls}`}>{tm.label}</span>
                      <span className={`badge ${sm.cls}`}>
                        {c.severity === "critical" ? <AlertTriangle size={11}/> : <Clock size={11}/>}
                        {c.severity.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-sm text-[#475569] mt-1">
                      <b className="text-red-700">{c.days_silent} days silent</b> · Normally orders every <b>~{c.avg_gap_days} days</b> · Last order on <span className="font-mono-num">{c.last_order_date}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
                      <div>
                        <div className="text-[9px] uppercase tracking-wider text-[#94A3B8] font-semibold">Total Orders</div>
                        <div className="font-mono-num font-bold">{c.total_orders}</div>
                      </div>
                      <div>
                        <div className="text-[9px] uppercase tracking-wider text-[#94A3B8] font-semibold">Lifetime Value</div>
                        <div className="font-mono-num font-bold text-[#C1121F]">{inr(c.lifetime_value)}</div>
                      </div>
                      <div>
                        <div className="text-[9px] uppercase tracking-wider text-[#94A3B8] font-semibold">Last Order</div>
                        <div className="font-mono-num font-bold">{inr(c.last_order_amount)}</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    {c.customer_phone && (
                      <a href={`tel:${c.customer_phone}`} className="px-3 py-1.5 rounded text-xs font-semibold bg-[#C1121F] text-white hover:bg-[#A00F19] flex items-center gap-1.5">
                        <Phone size={12}/> Call now
                      </a>
                    )}
                    {c.customer_phone && (
                      <a href={`https://wa.me/${c.customer_phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Namaste! Singh Offset se. ${c.days_silent} din se aapka order nahi aaya. Sab theek hai? Ek baar baat karen?`)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded text-xs font-semibold bg-green-600 text-white hover:bg-green-700 flex items-center gap-1.5">
                        <MessageCircle size={12}/> WhatsApp
                      </a>
                    )}
                    <Link to="/followups" className="px-3 py-1.5 rounded text-xs font-semibold bg-white border border-[#E5E7EB] text-[#475569] hover:border-[#C1121F] hover:text-[#C1121F] text-center">
                      Schedule
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
